import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Badge, Button, Dialog, Field, Icon, IconButton, Input, Spinner, TextArea, useLayout, useToast } from "@agentsaas/ui";
import { ERROR_CODES, type Agent, type ErrorCode, type Part } from "@agentsaas/protocol";
import { errorMessage } from "@agentsaas/i18n";
import { useErrorText, useT } from "../app/i18n";
import { useSession } from "../app/session";
import { formatMicroUsd, formatTime } from "../app/format";
import { useChat } from "./useChat";
import { visibleItems, type ChatItem } from "./model";
import { Markdown } from "./Markdown";
import { InlineDiff } from "./InlineDiff";
import { AssetRefList, assetRefsIn, collectAssetRefs } from "../assets/AssetRef";
import { stripRefLines } from "../assets/refs";
import { parseAssetRef } from "../rooms/model";
import { useQueryClient } from "@tanstack/react-query";
import { ck, useOpenQuestions } from "../data/collab";
import { QuestionCard } from "../questions/Questions";

/** 对话面板：接近 VS Code 里 Claude Code 的体验（architecture §8） */
export function ChatPanel({ agent, openFiles = [], inline }: { agent: Agent; openFiles?: string[]; inline?: ReactNode }) {
  const { t } = useT();
  const { state, loading, send, interrupt } = useChat(agent);
  const items = visibleItems(state);
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const running = state.running || agent.status === "busy" || agent.status === "waiting_approval";

  // 新内容到来时，若用户停留在底部则自动滚动
  useEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  });

  return (
    <section className="chat" aria-label={t("chat.title")} data-testid="chat-panel">
      <div className="pane-head">
        <span className="grow">{t("chat.title")}</span>
        {running ? <Spinner label={t("agent.status.busy")} /> : null}
      </div>
      <div
        className="chat__log"
        ref={scroller}
        role="log"
        aria-live="polite"
        aria-busy={running}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
        data-testid="chat-log"
      >
        {loading ? (
          <div className="chat__status muted small">{t("chat.history_loading")}</div>
        ) : items.length === 0 ? (
          <div className="chat__status muted">{t("chat.empty", { agent: agent.name })}</div>
        ) : (
          items.map((it) => <ChatRow key={it.id} item={it} pid={agent.projectId} agentName={agent.name} />)
        )}
        {inline}
      </div>
      <Composer agent={agent} running={running} onSend={send} onInterrupt={interrupt} openFiles={openFiles} />
    </section>
  );
}

function ChatRow({ item, pid, agentName }: { item: ChatItem; pid: string; agentName: string }) {
  const { t, locale } = useT();
  const user = useSession().state.user;
  const tz = user?.timezone;
  switch (item.kind) {
    case "user":
      return (
        <div className="msg msg--user" data-testid="msg-user">
          <div className="msg__meta small muted">
            {t("chat.you")} · <time dateTime={item.at}>{formatTime(item.at, locale, tz)}</time>
          </div>
          <div className="msg__bubble">
            {item.parts.map((p, i) =>
              p.type === "text" ? (
                <p key={i} className="msg__text">
                  {p.text}
                </p>
              ) : parseAssetRef(p.ref) ? null : (
                <Badge key={i} tone="accent">
                  <Icon name="paperclip" size={12} />
                  {p.name ?? p.ref}
                </Badge>
              ),
            )}
            <AssetRefList
              pid={pid}
              refs={collectAssetRefs(
                item.parts.flatMap((p) => (p.type === "ref" ? [p.ref] : [])),
                ...item.parts.map((p) => (p.type === "text" ? p.text : null)),
              )}
            />
          </div>
        </div>
      );
    case "text":
      return (
        <div className={`msg msg--agent${item.streaming ? " is-streaming" : ""}`} data-testid="msg-agent">
          <Markdown text={stripRefLines(item.text)} />
          {/* 流式输出中引用可能还没写完，结束后再预览 */}
          {!item.streaming ? <AssetRefList pid={pid} refs={assetRefsIn(item.text)} /> : null}
        </div>
      );
    case "thinking":
      return (
        <details className="thinking" data-testid="thinking">
          <summary>
            <Icon name="brain" size={14} />
            <span className="grow ellipsis">
              {item.streaming ? t("chat.thinking_streaming") : t("chat.thinking")}
              {` · ${item.text.slice(0, 80)}`}
            </span>
          </summary>
          <div className="thinking__body small">{item.text}</div>
        </details>
      );
    case "tool":
      return isAskHuman(item.name) ? <AskHumanCall item={item} pid={pid} agentName={agentName} /> : <ToolCall item={item} />;
    case "run":
      return <RunMarker item={item} />;
  }
}

function toolSummary(item: Extract<ChatItem, { kind: "tool" }>): string {
  const i = item.input;
  const v = i.command ?? i.file_path ?? i.path ?? i.pattern ?? i.url ?? "";
  return typeof v === "string" ? v : "";
}

function ToolCall({ item }: { item: Extract<ChatItem, { kind: "tool" }> }) {
  const { t } = useT();
  const status = !item.result ? "running" : item.result.isError ? "error" : "done";
  const isEdit = item.name === "Edit" && typeof item.input.old_string === "string" && typeof item.input.new_string === "string";
  const isWrite = item.name === "Write" && typeof item.input.content === "string";
  return (
    <details className={`tool tool--${status}`} open={isEdit || undefined} data-testid="tool-call" data-tool={item.name}>
      <summary>
        <Icon name="tool" size={14} />
        <strong>{item.name}</strong>
        <code className="grow ellipsis">{toolSummary(item)}</code>
        <Badge tone={status === "error" ? "danger" : status === "done" ? "success" : "accent"}>
          {status === "running" ? <Spinner label={t("chat.tool.running")} /> : null}
          {t(`chat.tool.${status}`)}
        </Badge>
      </summary>
      <div className="tool__body">
        {isEdit ? (
          <InlineDiff before={item.input.old_string as string} after={item.input.new_string as string} />
        ) : isWrite ? (
          <InlineDiff before="" after={item.input.content as string} />
        ) : (
          <>
            <div className="tool__label small muted">{t("chat.tool.input")}</div>
            <pre className="tool__pre">{JSON.stringify(item.input, null, 2)}</pre>
          </>
        )}
        {item.result ? (
          <>
            <div className="tool__label small muted">{t("chat.tool.output")}</div>
            <pre className={`tool__pre${item.result.isError ? " is-error" : ""}`}>{item.result.text}</pre>
          </>
        ) : null}
      </div>
    </details>
  );
}

/** ask_human（MCP 工具名形如 mcp__agentsaas__ask_human） */
export const isAskHuman = (name: string) => name === "ask_human" || name.endsWith("__ask_human");

/** 工具结果里的 questionId（ask_human 的输出 { questionId }） */
export function questionIdOf(result: { text: string } | undefined): string | null {
  if (!result) return null;
  try {
    const v = JSON.parse(result.text) as { questionId?: unknown };
    return typeof v.questionId === "string" ? v.questionId : null;
  } catch {
    return null;
  }
}

/**
 * 对话里的 ask_human：问题还开着就直接渲染成问题卡（选项按钮一键回答、资产预览）；
 * 已回答 / 找不到时退回普通的工具调用块。工具结果到达后刷新一次待回答列表。
 */
function AskHumanCall({ item, pid, agentName }: { item: Extract<ChatItem, { kind: "tool" }>; pid: string; agentName: string }) {
  const qc = useQueryClient();
  const open = useOpenQuestions(pid);
  const qid = questionIdOf(item.result);
  const done = !!item.result;
  useEffect(() => {
    if (done) void qc.invalidateQueries({ queryKey: ck.questions(pid) });
  }, [done, qc, pid]);
  const text = typeof item.input.question === "string" ? item.input.question : null;
  const q = open.data?.find((x) => (qid ? x.id === qid : x.question === text));
  if (q) return <QuestionCard question={q} agentName={agentName} pid={pid} inline />;
  return <ToolCall item={item} />;
}

function RunMarker({ item }: { item: Extract<ChatItem, { kind: "run" }> }) {
  const { t, locale } = useT();
  const text =
    item.status === "started"
      ? t("chat.run.started")
      : item.status === "finished"
        ? t("chat.run.finished", { cost: formatMicroUsd(item.costMicroUsd ?? 0, locale) })
        : item.status === "interrupted"
          ? t("chat.run.interrupted")
          : t("chat.run.error", { error: item.code && item.code in ERROR_CODES ? errorMessage(locale, { code: item.code as ErrorCode }) : (item.code ?? "") });
  return (
    <div className={`run run--${item.status}`} role="note" data-testid={`run-${item.status}`}>
      <span>{text}</span>
    </div>
  );
}

function Composer({ agent, running, onSend, onInterrupt, openFiles }: { agent: Agent; running: boolean; onSend(p: Part[]): Promise<void>; onInterrupt(): Promise<void>; openFiles: string[] }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const mobile = useLayout() === "mobile";
  const [text, setText] = useState("");
  const [refs, setRefs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  // 只有 runner 能接收指令时才允许发送（busy 时排队）；其他状态由状态提示说明原因
  const notReady = !["idle", "busy", "waiting_approval"].includes(agent.status);
  const canSend = !busy && !notReady && (text.trim().length > 0 || refs.length > 0);

  const submit = async () => {
    if (!canSend) return;
    const parts: Part[] = [
      ...(text.trim() ? [{ type: "text" as const, text: text.trim() }] : []),
      ...refs.map((p) => ({ type: "ref" as const, ref: `work://${p}`, name: p })),
    ];
    setBusy(true);
    try {
      await onSend(parts);
      setText("");
      setRefs([]);
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // 桌面：Enter 发送、Shift+Enter 换行；手机：Enter 换行，用发送按钮
    if (e.key === "Enter" && !e.shiftKey && !mobile && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className="composer">
      {refs.length ? (
        <div className="composer__refs">
          {refs.map((r) => (
            <span key={r} className="chip">
              <Icon name="paperclip" size={12} />
              <span className="ellipsis">{r}</span>
              <button type="button" className="chip__x" aria-label={t("chat.remove_ref", { name: r })} onClick={() => setRefs((xs) => xs.filter((x) => x !== r))}>
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {notReady ? <div className="small muted">{t("chat.agent_not_ready")}</div> : null}
      <div className="composer__row">
        <IconButton icon="paperclip" label={t("chat.attach")} onClick={() => setAttachOpen(true)} />
        <TextArea
          className="composer__input"
          rows={mobile ? 1 : 2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder={t("chat.placeholder", { agent: agent.name })}
          aria-label={t("chat.placeholder", { agent: agent.name })}
          aria-describedby="composer-hint"
          data-testid="chat-input"
        />
        {running ? (
          <IconButton icon="stop" label={t("chat.interrupt")} variant="danger" onClick={() => void onInterrupt().catch((e) => toast.show({ message: errText(e), tone: "danger" }))} data-testid="chat-interrupt" />
        ) : null}
        <IconButton icon="send" label={t("chat.send")} variant="primary" disabled={!canSend} onClick={() => void submit()} data-testid="chat-send" />
      </div>
      {!mobile ? (
        <div id="composer-hint" className="small muted composer__hint">
          {t("chat.send_hint")}
        </div>
      ) : null}
      <AttachDialog
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        openFiles={openFiles}
        onAdd={(p) => {
          setRefs((xs) => (xs.includes(p) ? xs : [...xs, p]));
          setAttachOpen(false);
        }}
      />
    </div>
  );
}

function AttachDialog({ open, onClose, openFiles, onAdd }: { open: boolean; onClose(): void; openFiles: string[]; onAdd(p: string): void }) {
  const { t } = useT();
  const [path, setPath] = useState("");
  const valid = path.trim() && !path.startsWith("/") && !path.split("/").includes("..");
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("chat.attach.title")}
      closeLabel={t("shell.close")}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!valid} onClick={() => onAdd(path.trim())}>
            {t("chat.attach.add")}
          </Button>
        </>
      }
    >
      <Field label={t("chat.attach.path")}>{(id) => <Input id={id} value={path} onChange={(e) => setPath(e.target.value)} data-autofocus />}</Field>
      {openFiles.length ? (
        <>
          <div className="ui-field__label">{t("chat.attach.open_files")}</div>
          <ul className="list">
            {openFiles.map((f) => (
              <li key={f}>
                <button type="button" className="list-item" onClick={() => onAdd(f)}>
                  <Icon name="file" size={14} />
                  <span className="ellipsis">{f}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Dialog>
  );
}
